/**
 * English - `tipsWhatsapp`: the ⓘ explanations beside WhatsApp Commerce fields
 * (settings, connection wizard, templates, automations, campaigns, inbox
 * dialogs). One key per field, `<form>.<field>`, passed as the field's `info`.
 * Conventions: see `common.ts`.
 */
const tipsWhatsapp: Record<string, string> = {
  // >>> settings: storefront entry points
  'settings.buy_on_whatsapp':
    'Adds a Buy on WhatsApp button to every product page. Tapping it opens a chat with your number, with the Buy on WhatsApp message below already typed in.',
  'settings.chat_on_whatsapp':
    'Shows a floating chat button on every page of your store. Tapping it opens a WhatsApp chat with your number, with the support greeting below already typed in.',
  'settings.share_on_whatsapp':
    'Adds a share link on product pages, so customers can send a product to friends and family on WhatsApp.',
  'settings.help_on_cart_checkout':
    'Shows a "Need help? Chat on WhatsApp" link near the checkout button, so customers with a question can reach you before they pay.',
  'settings.continue_checkout_on_whatsapp':
    'When on, customers can move their cart into a WhatsApp chat and finish the order there instead of on your website. Off by default.',

  // >>> settings: messages
  'settings.welcome_message':
    'The first message the shopping bot sends, with its menu, when a customer starts a chat. The store name placeholder becomes your shop name. If the Welcome automation is on with its own message, that is used instead. Up to 500 characters.',
  'settings.support_greeting':
    'The text already typed in the customer’s WhatsApp when they tap the chat button on your store. They can change it before sending. Up to 500 characters. Example: Hi! I have a question about my order.',
  'settings.buy_prefill':
    'The text already typed in the customer’s WhatsApp when they tap Buy on WhatsApp on a product page. The product name placeholder becomes that product’s name. Up to 500 characters.',
  'settings.default_language':
    'The language your WhatsApp channel uses by default, as a short language code of 2 to 8 characters. Left blank, English (en) is used. Example: hi',

  // >>> settings: shopping assistant
  'settings.bot_enabled':
    'When on, an automatic assistant replies to customers with menus, lets them browse products and place an order inside the chat. Turn it off if your team wants to answer every message by hand.',
  'settings.ai_assistant_enabled':
    'When on, questions customers type in their own words are answered automatically, using only the products in your catalog. Needs a plan that includes the AI assistant.',
  'settings.order_updates_enabled':
    'When on, customers get WhatsApp messages as their order is confirmed, shipped and delivered.',
  'settings.catalog_auto_sync':
    'When on, product changes you make in your store are sent to your WhatsApp catalog as they happen. When off, use the sync button on the Catalog tab.',
  'settings.catalog_message_enabled':
    'When on, the bot sends WhatsApp’s own "View catalog" message with its menu once your products are synced, so customers can browse and build a cart without leaving the chat.',
  'settings.abandoned_cart_delay':
    'How long to wait after a customer leaves items in their cart before the reminder goes out. Enter minutes, from 15 up to 1440 (24 hours). Reminders only go to customers who agreed to promotional messages. Example: 60',

  // >>> settings: payments, data, quick replies
  'settings.payments_enabled':
    'When on, customers pay for their order inside the WhatsApp chat instead of opening a payment link on your store. India only: needs an Indian number, prices in ₹ and the payment configuration name below.',
  'settings.payment_configuration':
    'The exact name of the payment configuration you created in WhatsApp Manager under Payments, linked to your Razorpay account. Required when in-chat payments are on. Up to 120 characters.',
  'settings.message_retention':
    'Chat messages and files customers send are deleted automatically once they are older than this many days. Enter 0 to keep everything, or from 30 to 3650 days. Example: 365',
  'settings.quick_reply_title':
    'A short name your team picks from the quick replies list in the inbox. Up to 40 characters. Example: Delivery time',
  'settings.quick_reply_body':
    'The text sent to the customer when your team picks this quick reply. Up to 1000 characters. Example: Orders in Mumbai are delivered in 2 to 3 working days.',

  // >>> connect: token form
  'connect.access_token':
    'A permanent system-user token with WhatsApp permissions. It must be issued through this platform’s Meta app, or messages will never arrive. It is stored encrypted and never shown again.',
  'connect.phone_number_id':
    'The ID Meta gives your WhatsApp number, not the phone number itself. Find it in your Meta app under WhatsApp, then API Setup. Numbers only. Example: 123456789012345',
  'connect.waba_id':
    'The ID of your WhatsApp Business Account (WABA), shown on the same WhatsApp, API Setup page of your Meta app. Numbers only.',
  'connect.business_id':
    'The ID of the Meta business that owns your WhatsApp account, from Business Settings, then Business info. A number, not the business name. Optional.',

  // >>> connect: wizard steps
  'connect.pin':
    'The six-digit two-step verification PIN for this number, set in WhatsApp Manager. If none is set yet, the six digits you enter become the PIN. It is sent to Meta once and not stored here.',
  'profile.about':
    'A short line about your business shown on your WhatsApp profile. Up to 139 characters. Optional. Example: Handmade cotton sarees from Jaipur',
  'profile.email':
    'The email address customers see on your WhatsApp business profile. Up to 128 characters. Optional. Example: support@yourshop.in',
  'profile.address':
    'Your shop or office address, shown on your WhatsApp business profile. Up to 256 characters. Optional. Example: 12 MG Road, Bengaluru 560001',
  'profile.vertical':
    'The industry your business is in, shown on your WhatsApp business profile. Pick from Meta’s list. Optional. Example: Clothing and apparel',
  'profile.description':
    'A longer introduction to your business, shown on your WhatsApp profile. Up to 512 characters. Optional.',
  'profile.websites':
    'Links shown on your WhatsApp business profile, one per line. Up to two websites. Optional. Example: https://yourshop.in',
  'catalog.name':
    'The name of the new Meta catalog your products are published to. Left blank, your store name followed by "catalogue" is used. Up to 160 characters. Example: Sharma Sarees catalogue',
  'catalog.catalog_id':
    'The ID of a catalog you already have, from Meta Commerce Manager under Catalog, then Settings. Numbers only, not the catalog name. Your products are published into it.',

  // >>> templates
  'template.name':
    'An internal name Meta uses for this template; customers do not see it. Use lowercase letters, numbers and underscores only (spaces and dashes become underscores). It cannot be changed once submitted to Meta. Example: order_shipped_v2',
  'template.language':
    'The language the message is written in, as a WhatsApp language code: en is English, en_US is English (US), hi is Hindi. Pick the one that matches the body text. Example: hi',
  'template.category':
    'Tells Meta what kind of message this is. Utility is for order and account updates, Marketing for offers and promotions, Authentication for one-time codes. Campaigns can only use Marketing templates, and Meta may change the category if the text does not match.',
  'template.header_format':
    'An optional top part of the message. Text shows a bold line above the body; Image, Video and Document show media at the top.',
  'template.header_text':
    'The bold line shown above the message. Up to 60 characters, with at most one variable. Example: Your order update',
  'template.header_media':
    'The image, video or PDF shown at the top of every message sent with this template. WhatsApp accepts JPEG or PNG images up to 5 MB, MP4 videos up to 16 MB and PDF documents up to 16 MB. It is also the sample Meta reviews when you send the template for approval.',
  'template.body':
    'The main message customers receive. Use Insert variable to add details filled in for each customer, like their name or order number. Meta reviews this text before the template can be sent. Up to 1024 characters.',
  'template.footer':
    'A small grey line shown under the message. Optional, up to 60 characters. Example: Thank you for shopping with us',
  'template.button_type':
    'What the button does. Quick reply lets the customer answer with the button’s label, Visit website opens a link and Call dials a number. Up to three buttons, at most two of them links.',
  'template.button_text':
    'The words on the button. Up to 25 characters. Example: Track order',
  'template.button_url':
    'The web address the button opens. It must start with http:// or https://. Example: https://yourshop.in/orders',
  'template.button_phone':
    'The number the button calls, with country code, 8 to 15 digits. Example: +919876543210',

  // >>> automations
  'automation.template':
    'The approved template this automation sends; a template can reach customers at any time. With None, the plain message below goes out only if the customer wrote to you in the last 24 hours. Some automations need a template.',
  'automation.delay':
    'How many minutes to wait after the customer’s last message before the cart reminder goes out. Enter 15 to 1440 (24 hours). Example: 60',
  'automation.message':
    'Plain text sent when no template is chosen, only to customers who wrote to you in the last 24 hours. It can use the same placeholders as templates, like the customer name or order number. Up to 1024 characters.',

  // >>> campaigns
  'campaign.name':
    'A name to find this campaign in your list; customers do not see it. At least 2 characters. Example: Diwali sale 2026',
  'campaign.template':
    'The message to send. Only approved templates in the Marketing category are listed, because Meta allows only those for promotions.',
  'campaign.audience':
    'Which customers receive the campaign. Only customers who agreed to promotional messages are ever included; the options narrow that further, for example to repeat buyers or customers with no order in 60 days.',
  'campaign.variable':
    'The text that fills this placeholder in the template. Everyone gets the same value, except the customer name, which uses each customer’s own name when left blank.',
  'campaign.schedule_for':
    'The date and time the campaign starts sending, in your device’s local time. A time in the past sends it right away.',

  // >>> inbox
  'inbox.opt_in':
    'Marks whether this customer agreed to receive promotional messages. Only opted-in customers get campaigns, welcome messages and cart reminders. Switch it on only with their permission.',
  'inbox.template_select':
    'An approved template to send. Templates are the only messages WhatsApp allows once 24 hours have passed since the customer last wrote to you.',
  'inbox.template_variable':
    'The text that fills this placeholder in the message for this customer. Example: ORD-1042',
  'inbox.product_variant':
    'The exact option to send, such as a size or colour. Pick "Let the customer choose" to send the product with all its options.',
  'inbox.product_message':
    'A short note sent along with the product. Optional, up to 1024 characters. Example: This is the kurta we spoke about, available in M and L.',
};

export default tipsWhatsapp;
